<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    title="Income"
    :view-only="viewOnly"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="3">
          <b>Household Monthly Income: ${{ householdMonthly }}</b>
        </v-col>
        <v-col cols="3">
          <b>Household Yearly Income: ${{ householdYearly }}</b>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.monthly_household_income_estimated"
            autocomplete="off"
            label="Household Monthly Estimated"
            outlined
            prefix="$"
            type="number"
            :disabled="estimationDisabled"
            :error-messages="getErrorMessages(v$.section.monthly_household_income_estimated)"
            @input="v$.section.monthly_household_income_estimated.$touch()"
            @blur="v$.section.monthly_household_income_estimated.$touch()"
          />
        </v-col>
        <v-col
          cols="1"
          class="text-center mt-4"
        >
          <b>OR</b>
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.monthly_income"
            autocomplete="off"
            label="Party Monthly Income"
            outlined
            prefix="$"
            type="number"
            :error-messages="getErrorMessages(v$.section.monthly_income)"
            @input="v$.section.monthly_income.$touch()"
            @blur="v$.section.monthly_income.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.income_verification"
            :items="optionsIncomeVerification"
            label="Verification"
            outlined
            :error-messages="getErrorMessages(v$.section.income_verification)"
            @input="v$.section.income_verification.$touch()"
            @blur="v$.section.income_verification.$touch()"
          />
        </v-col>
      </v-row>
      <MarriagesIncomeRow
        v-for="spouse of section.spouses"
        :key="spouse.id"
        :item="spouse"
        class="mb-4"
      />
      <ChildrenIncomeRow
        v-for="child of section.children"
        :key="child.id"
        :item="child"
        class="mb-4"
      />
      <ParentsIncomeRow
        v-for="parent of section.parents"
        :key="parent.id"
        :item="parent"
        class="mb-4"
      />
      <SiblingsIncomeRow
        v-for="sibling of section.siblings"
        :key="sibling.id"
        :item="sibling"
        class="mb-4"
      />
    </v-form>
    <v-row>
      <v-col>
        <i>To enter additional household incomes, go to the Family tab and add household members first, then they will appear in this section.</i>
      </v-col>
    </v-row>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import ChildrenIncomeRow from '@/components/Party/PartyEdit/SectionCards/FamiliesCards/Integrations/ChildrenIncomeRow'
import MarriagesIncomeRow from '@/components/Party/PartyEdit/SectionCards/FamiliesCards/Integrations/MarriagesIncomeRow'
import ParentsIncomeRow from '@/components/Party/PartyEdit/SectionCards/FamiliesCards/Integrations/ParentsIncomeRow'
import SiblingsIncomeRow from '@/components/Party/PartyEdit/SectionCards/FamiliesCards/Integrations/SiblingsIncomeRow'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'IncomeCard',
  components: { ChildrenIncomeRow, GenericCard, MarriagesIncomeRow, ParentsIncomeRow, SiblingsIncomeRow },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },

    viewOnly: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        income_verification: {},
        monthly_household_income_estimated: {},
        monthly_income: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return 'income-card' },
    estimationDisabled () { return Number(this.section.monthly_income) > 0 },

    householdMonthly () {
      const sectionIncome = Number(this.section.monthly_income || this.section.monthly_household_income_estimated)
      return sectionIncome +
            this.section?.spouses?.reduce((total, spouse) => total + Number(spouse.monthly_income), 0) || 0 +
            this.section?.children?.reduce((total, child) => total + Number(child.monthly_income), 0) || 0 +
            this.section?.parents?.reduce((total, parent) => total + Number(parent.monthly_income), 0) || 0 +
            this.section?.siblings?.reduce((total, sibling) => total + Number(sibling.monthly_income), 0) || 0
    },

    householdYearly () { return this.householdMonthly * 12 },

    locked: {
      get () {
        return this.PeIncomeCardLock
      },

      set (value) {
        this.setPeIncomeCardLock(value)
      },
    },

    optionsIncomeVerification () { return this.getCachedDropdown('parties/income_verification') },

    ...mapGetters(PARTY_NAMESPACE, [
      'PeIncomeCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'section.monthly_income' (val) {
      if (Number(val) > 0) this.section.monthly_household_income_estimated = null
    },
  },

  created () {
    this.initPeIncomeCard()
    this.getDropdownOptions({ path: 'parties/income_verification' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardRefresh () {
      return this.PeIncomeCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.PeIncomeCardUpdate({ id: this.peParty.id, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeIncomeCardLock(false)
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPeIncomeCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'PeIncomeCardRefresh',
      'PeIncomeCardUpdate',
      'initPeIncomeCard',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
